import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import axios from 'axios';
import { Sparkles, Mic, MicOff, Volume2, VolumeX, Send, X, GripVertical } from 'lucide-react';
import { useOnboarding } from '../onboarding/OnboardingContext';
import { useCredits } from '../hooks/useCredits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS_KEY = 'metis.widget.pos.v1';
const SID_KEY = 'metis.widget.sid.v1';
const MSGS_KEY = 'metis.widget.msgs.v1';

function loadPos() {
    try { return JSON.parse(localStorage.getItem(POS_KEY)) || null; } catch { return null; }
}
function loadMsgs() {
    try { return JSON.parse(localStorage.getItem(MSGS_KEY)) || []; } catch { return []; }
}

// SpeechRecognition polyfill
const SR = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const canSpeak = typeof window !== 'undefined' && 'speechSynthesis' in window;

export default function MetisWidget({ defaultOpen = false, panelHint = null }) {
    const { user } = useOnboarding();
    const { spend, out, grant } = useCredits();
    const profile = useMemo(() => ({
        name: user?.name,
        board_class: user?.board_class || 'CBSE Class 10',
        class_level: user?.class_level,
        primary_exam_track: user?.primary_exam_track,
        additional_national_exams: user?.additional_national_exams || [],
        subscription_tier: user?.subscription_tier || (user?.is_guest ? 'free' : undefined),
        focus_areas: user?.focus_areas || [],
        primary_objective: user?.primary_objective,
        time_commitment: user?.time_commitment,
    }), [user]);

    const [open, setOpen] = useState(defaultOpen);
    const [pos, setPos] = useState(() => loadPos() || { x: 0, y: 0 });
    const [messages, setMessages] = useState(() => loadMsgs());
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [listening, setListening] = useState(false);
    const [ttsOn, setTtsOn] = useState(false);
    const [sessionId, setSessionId] = useState(() => localStorage.getItem(SID_KEY) || null);
    const recRef = useRef(null);
    const scrollRef = useRef(null);
    const dragControls = useDragControls();

    useEffect(() => { localStorage.setItem(POS_KEY, JSON.stringify(pos)); }, [pos]);
    useEffect(() => { localStorage.setItem(MSGS_KEY, JSON.stringify(messages.slice(-40))); }, [messages]);
    useEffect(() => { if (sessionId) localStorage.setItem(SID_KEY, sessionId); }, [sessionId]);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    // Auto-greeting on first open
    useEffect(() => {
        if (open && messages.length === 0) {
            const greet = user?.name
                ? `Hi ${user.name.split(' ')[0]} — I'm Metis. Ready when you are. Ask me anything about your NCERT chapters, or say "quiz me on <topic>".`
                : "Hi — I'm Metis, your study advisor. Tell me what you'd like to work on today.";
            setMessages([{ role: 'metis', text: greet, at: Date.now() }]);
        }
    }, [open]); // eslint-disable-line

    const speak = useCallback((text) => {
        if (!canSpeak || !ttsOn || !text) return;
        try {
            const u = new SpeechSynthesisUtterance(text);
            u.rate = 1.02; u.pitch = 1.0; u.volume = 0.95;
            const voices = window.speechSynthesis.getVoices();
            const pick = voices.find((v) => /en-IN|en-GB|en-US/i.test(v.lang) && /female|natural|neural/i.test(v.name))
                || voices.find((v) => /en/i.test(v.lang));
            if (pick) u.voice = pick;
            window.speechSynthesis.cancel();
            window.speechSynthesis.speak(u);
        } catch {}
    }, [ttsOn]);

    async function send(text) {
        const clean = (text || '').trim();
        if (!clean || sending) return;
        // Enforce credits before hitting the API.
        if (out || !spend(1)) {
            setMessages((m) => [...m, { role: 'metis', text: 'You\u2019re out of credits — tap "Buy more credits" in the notice to keep chatting.', at: Date.now(), err: true }]);
            return;
        }
        setInput('');
        setMessages((m) => [...m, { role: 'user', text: clean, at: Date.now() }]);
        setSending(true);
        try {
            const r = await axios.post(`${API}/tutor/chat`, {
                session_id: sessionId,
                message: clean,
                profile,
                mode: 'chat',
            });
            const reply = r.data?.reply || 'I lost the thread — try again?';
            setSessionId(r.data?.session_id || sessionId);
            setMessages((m) => [...m, { role: 'metis', text: reply, at: Date.now() }]);
            speak(reply);
        } catch (e) {
            // Refund the credit on failure — especially AI quota outages (503 metis_quota_exhausted)
            grant(1);
            const quota = e?.response?.data?.detail === 'metis_quota_exhausted';
            setMessages((m) => [...m, {
                role: 'metis',
                text: quota
                    ? 'Metis AI quota is temporarily exhausted (billing). Your credit was refunded — please try again soon.'
                    : 'Metis is thinking too hard right now. Your credit was refunded — try in a moment.',
                at: Date.now(), err: true,
            }]);
        } finally {
            setSending(false);
        }
    }

    function startListening() {
        if (!SR) {
            alert('Voice input isn\u2019t supported in this browser. Try Chrome, Edge, or Safari.');
            return;
        }
        if (listening) return;
        const rec = new SR();
        rec.lang = 'en-IN';
        rec.interimResults = true;
        rec.maxAlternatives = 1;
        rec.continuous = false;
        let finalText = '';
        rec.onresult = (ev) => {
            for (let i = ev.resultIndex; i < ev.results.length; i++) {
                const r = ev.results[i];
                if (r.isFinal) finalText += r[0].transcript;
                else setInput((prev) => (prev ? prev.split(' [\u2026] ')[0] : '') + ' [\u2026] ' + r[0].transcript);
            }
            if (finalText) setInput(finalText.trim());
        };
        rec.onend = () => {
            setListening(false);
            if (finalText.trim()) send(finalText.trim());
        };
        rec.onerror = () => setListening(false);
        recRef.current = rec;
        setListening(true);
        rec.start();
    }
    function stopListening() {
        try { recRef.current?.stop(); } catch {}
        setListening(false);
    }

    function clearChat() {
        setMessages([]);
        setSessionId(null);
        localStorage.removeItem(SID_KEY);
        localStorage.removeItem(MSGS_KEY);
    }

    // Sync external prompts (Acropolis "Ask Metis about this concept")
    useEffect(() => {
        if (panelHint) {
            setOpen(true);
            send(panelHint);
        }
        // eslint-disable-next-line
    }, [panelHint]);

    // Global listener so any page can programmatically prompt Metis
    useEffect(() => {
        const handler = (e) => {
            setOpen(true);
            if (e?.detail?.prompt) send(e.detail.prompt);
        };
        window.addEventListener('metis:open', handler);
        return () => window.removeEventListener('metis:open', handler);
        // eslint-disable-next-line
    }, []);

    return (
        <motion.div
            drag
            dragListener={false}
            dragControls={dragControls}
            dragMomentum={false}
            dragElastic={0}
            onDragEnd={(_, info) => setPos((p) => ({ x: p.x + info.offset.x, y: p.y + info.offset.y }))}
            style={{ x: pos.x, y: pos.y }}
            className="fixed bottom-6 right-6 z-[200] select-none"
            data-testid="metis-widget"
        >
            <AnimatePresence>
                {open ? (
                    <motion.div
                        key="panel"
                        initial={{ opacity: 0, y: 12, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.96 }}
                        transition={{ duration: 0.3 }}
                        className="w-[360px] sm:w-[400px] max-h-[70vh] flex flex-col rounded-lg overflow-hidden"
                        style={{
                            background:
                                'linear-gradient(160deg, rgba(255,255,255,0.85) 0%, rgba(245,240,255,0.78) 100%)',
                            backdropFilter: 'blur(20px) saturate(150%)',
                            WebkitBackdropFilter: 'blur(20px) saturate(150%)',
                            border: '1px solid rgba(59,68,30,0.35)',
                            boxShadow: '0 30px 70px -20px rgba(11,15,7,0.55), 0 10px 30px -10px rgba(0,0,0,0.35)',
                        }}
                        data-testid="metis-panel"
                    >
                        {/* Header (drag handle) */}
                        <div
                            onPointerDown={(e) => dragControls.start(e)}
                            className="flex items-center gap-2 px-4 py-3 cursor-move"
                            style={{ background: 'var(--moss-deep)', color: 'var(--parchment)' }}
                            data-testid="metis-drag-handle"
                        >
                            <GripVertical size={14} className="opacity-60" />
                            <Sparkles size={14} className="text-[color:var(--lavender)]" />
                            <div className="flex-1">
                                <div className="mono text-[10px] tracking-widest">METIS</div>
                                <div className="mono text-[9px] opacity-70">AI Study Advisor &amp; Mentor</div>
                            </div>
                            <button
                                onClick={() => setTtsOn((v) => !v)}
                                title={ttsOn ? 'Voice replies: on' : 'Voice replies: off'}
                                data-testid="metis-tts-toggle"
                                className="p-1.5 rounded hover:bg-white/10"
                            >
                                {ttsOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                            </button>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="metis-close"
                                className="p-1.5 rounded hover:bg-white/10"
                                aria-label="Close Metis"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        {/* Message list */}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto px-4 py-3 space-y-2"
                            data-testid="metis-messages"
                        >
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`text-[13px] leading-snug px-3 py-2 rounded-[10px] ${
                                        m.role === 'user'
                                            ? 'ml-auto max-w-[85%] bg-[color:var(--lavender)]/70 text-[color:var(--ink)]'
                                            : 'mr-auto max-w-[92%] bg-white/70 border border-[color:var(--moss-deep)]/20 text-[color:var(--ink)]'
                                    }`}
                                    data-testid={`metis-msg-${m.role}`}
                                >
                                    {m.text.split('\n').map((line, li) => (
                                        <div key={li}>{line || '\u00a0'}</div>
                                    ))}
                                </div>
                            ))}
                            {sending && (
                                <div className="mr-auto text-[11px] mono text-[color:var(--moss-deep)]/70" data-testid="metis-typing">
                                    Metis is thinking…
                                </div>
                            )}
                        </div>

                        {/* Composer */}
                        <div className="border-t border-[color:var(--moss-deep)]/25 px-3 py-2 flex items-center gap-2 bg-white/50">
                            <button
                                onClick={listening ? stopListening : startListening}
                                title={listening ? 'Stop listening' : 'Push to talk'}
                                data-testid="metis-mic"
                                className={`p-2 rounded-full transition-all ${
                                    listening
                                        ? 'bg-[color:var(--lavender-deep)] text-white animate-pulse'
                                        : 'bg-[color:var(--moss-deep)]/10 text-[color:var(--moss-deep)] hover:bg-[color:var(--moss-deep)]/20'
                                }`}
                            >
                                {listening ? <MicOff size={14} /> : <Mic size={14} />}
                            </button>
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && send(input)}
                                placeholder={listening ? 'Listening\u2026' : 'Ask Metis anything\u2026'}
                                className="flex-1 bg-transparent outline-none text-[13px] text-[color:var(--ink)] placeholder:text-[color:var(--moss-deep)]/60 px-1"
                                data-testid="metis-input"
                            />
                            <button
                                onClick={() => send(input)}
                                disabled={sending || !input.trim()}
                                data-testid="metis-send"
                                className="p-2 rounded-full bg-[color:var(--lavender-deep)] text-white disabled:opacity-40"
                            >
                                <Send size={14} />
                            </button>
                        </div>
                        <div className="px-4 pb-2 pt-1 flex items-center justify-between">
                            <button
                                onClick={clearChat}
                                className="mono text-[9px] text-[color:var(--moss-deep)]/70 hover:text-[color:var(--moss-deep)]"
                                data-testid="metis-clear"
                            >
                                clear chat
                            </button>
                            <div className="mono text-[9px] text-[color:var(--moss-deep)]/50">
                                {SR ? 'voice ready' : 'voice unsupported'}
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <motion.button
                        key="bubble"
                        onPointerDown={(e) => {
                            // Only start drag if user long-presses / uses grip. Otherwise treat as click.
                            if (e.pointerType === 'mouse' && e.button !== 0) return;
                        }}
                        initial={{ opacity: 0, scale: 0.6 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.6 }}
                        onClick={() => setOpen(true)}
                        className="relative h-14 w-14 rounded-full flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)]"
                        style={{
                            background: 'linear-gradient(140deg, var(--moss-deep) 0%, var(--moss-dark) 100%)',
                            border: '2px solid var(--lavender)',
                        }}
                        data-testid="metis-bubble"
                        title="Chat with Metis"
                    >
                        <motion.div
                            animate={{ scale: [1, 1.15, 1] }}
                            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
                            className="absolute inset-0 rounded-full"
                            style={{ boxShadow: '0 0 24px rgba(183,156,255,0.55)' }}
                        />
                        <Sparkles size={22} className="text-[color:var(--lavender)] relative z-10" />
                        <span
                            onPointerDown={(e) => { e.stopPropagation(); dragControls.start(e); }}
                            className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-[color:var(--parchment)] border border-[color:var(--moss-deep)] flex items-center justify-center cursor-move"
                            title="Drag to reposition"
                            data-testid="metis-bubble-drag"
                        >
                            <GripVertical size={10} className="text-[color:var(--moss-deep)]" />
                        </span>
                    </motion.button>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
